import * as logMessageTypes from '../../consts/app/logMessageTypes';
import gamTimeouts from '../../consts/config/gamTimeouts';
import * as gamMethods from '../../consts/gam/gamMethods';
import * as yapActionStatusTypes from '../../consts/yap/yapActionStatusTypes';
import * as yapFlowTypes from '../../consts/yap/yapFlowTypes';
import * as yapRunCommandActionTypes from '../../consts/yap/yapRunCommandActionTypes';
import {setFetchTokenInProgress} from '../../state/ducks/auth';
import {
    setYapAssets,
    setYapLastRequestId,
    setYapRequestActionTypeData,
    setYapUpdateSettingsInProgress,
} from '../../state/ducks/yapEncrypted';
import {makeSelectGamAuthToken, makeSelectHolderGamAuthToken} from '../../state/selectors/auth';
import {makeSelectAssetIdByType, makeSelectIsYapUpdateSettingsInProgress} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import appErrorService from '../app/appErrorService';
import cmService from '../communicationLayer/cmService';
import log from '../logger/log';
import {gamAssetMapping} from '../mapping/gamMappings';
import yapClientService from '../yap/yapClientService';
import yapConnectionService from '../yap/yapConnectionService';
import yapFlowTimeoutService from '../yap/yapFlowTimeoutService';
import yapStateService from '../yap/yapStateService';
import GamClient from './gamClient';
import * as gamGigyaService from './gamGigyaService';

const initGam = async (isHolder) => {
    try {
        //seems like functionality is removed because of security dce2 decision
        /*
        const dataFactory = domService.getDeviceDataFactoryFromRoot();;

        if (dataFactory === undefined) {
            log.error(`gamService: initGam, error: dataFactory is undefined`, logMessageTypes.GAM);
            appRouterService.forwardToErrorHandlingPage(errorPageTypes.GAM_SYSTEM_ERROR);
            return;
        }*/
        const state = getState();
        const gamAuthToken = isHolder ? makeSelectHolderGamAuthToken()(state) : makeSelectGamAuthToken()(state);

        if (gamAuthToken || (await gamGigyaService.fetchGamToken(isHolder))) {
            dispatch(setFetchTokenInProgress(false));
            return await getGamAssets(isHolder);
        }
    } catch (e) {
        log.error(`gamService: initGam, error: ${e}`, logMessageTypes.GAM);
    }
    return false;
};

const getGamAssets = async (isHolder) => {
    const options = {
        methodName: gamMethods.GET_ASSETS,
        isHolder,
        isNeedRedirectForOtherErrors: false,
    };

    const response = await new GamClient().callPost(options);

    if (response !== null) {
        const assets = gamAssetMapping(response, isHolder);

        dispatch(setYapAssets(assets));

        yapStateService.updateAssetMedia(assets);
        return true;
    }
    return false;
};

const connectGamDevice = (assetId) =>
    new GamClient().callGet({
        methodName: gamMethods.CONNECT_DEVICE,
        requestConfig: {args: assetId},
        isNeedRedirectForOtherErrors: false,
    });

const getGamDeviceInfo = async (assetId) => {
    if (!assetId) return false;

    const deviceInfo = await new GamClient().callGet({
        methodName: gamMethods.GET_DEVICE_INFO,
        requestConfig: {args: assetId},
        isNeedRedirectForOtherErrors: false,
    });

    if (!deviceInfo) return false;

    return yapStateService.updateIotDevice(assetId, deviceInfo);
};

const updateGamSettings = async (body, isHolder) => {
    const isYapUpdateSettingsInProgress = makeSelectIsYapUpdateSettingsInProgress(isHolder)(getState());

    if (isYapUpdateSettingsInProgress) return;

    yapFlowTimeoutService.clearFlowTimeout(yapFlowTypes.UPDATE_SETTINGS, isHolder);

    dispatch(setYapUpdateSettingsInProgress(true, isHolder));
    dispatch(setYapRequestActionTypeData({isHolder}));

    const state = getState();
    const assetId = makeSelectAssetIdByType(isHolder)(state);

    if (assetId) {
        try {
            await yapConnectionService.syncAndSubscribeOnDevice(isHolder);
            const isConnected = await cmService.waitUntilConnected({isHolder});

            if (!isConnected) {
                appErrorService.showDeviceSettingsError(isHolder);
                dispatch(setYapUpdateSettingsInProgress(false, isHolder));
                return;
            }

            log.info('Start updateGamSettings process.');

            const {requestId} =
                (await new GamClient().callPost({
                    methodName: gamMethods.UPDATE_SETTINGS,
                    requestConfig: {args: assetId, data: body},
                    isNeedRedirectForOtherErrors: false,
                })) || {};

            if (requestId) {
                dispatch(setYapLastRequestId(requestId, isHolder));

                dispatch(
                    setYapRequestActionTypeData({
                        isHolder,
                        actionType: yapRunCommandActionTypes.UPDATE_SETTINGS,
                        newValue: body,
                    })
                );

                yapFlowTimeoutService.createFlowTimeout({
                    type: yapFlowTypes.UPDATE_SETTINGS,
                    timeoutInSec: gamTimeouts.GAM_UPDATE_SETTING_TIMEOUT,
                    isHolder,
                });
                return;
            }
        } catch (e) {
            log.error(`gamClientService: updateGamSettings failed. error: ${e}`, logMessageTypes.GAM);
        }
    }

    appErrorService.showDeviceSettingsGlobalError();
    dispatch(setYapUpdateSettingsInProgress(false, isHolder));
};

const checkGamRequestStatus = async (assetId, requestId, isSync = false) => {
    const data = await new GamClient().callGet({
        methodName: gamMethods.CHECK_REQUEST_STATUS,
        requestConfig: {args: [assetId, requestId]},
        isNeedRedirectForOtherErrors: false,
    });

    const isFailed = data?.status === yapActionStatusTypes.FAILED;
    const isSuccess = data?.status === yapActionStatusTypes.SUCCESS;

    if (isSuccess || isFailed) {
        if (!isSync) {
            const isYapUpdateSettingsInProgress = makeSelectIsYapUpdateSettingsInProgress()(getState());

            if (isYapUpdateSettingsInProgress && isFailed) {
                appErrorService.showDeviceSettingsGlobalError();
            }
        }

        return data;
    }

    return {status: yapActionStatusTypes.PENDING};
};

const resetMyDevice = async (isHolder) => {
    return await yapClientService.runYapCommand({
        isHolder,
        methodName: gamMethods.RESET_DEVICE,
        actionType: yapRunCommandActionTypes.RESET_DEVICE,
    });
};

export default {
    initGam,
    connectGamDevice,
    updateGamSettings,
    getGamDeviceInfo,
    checkGamRequestStatus,
    resetMyDevice,
};
